import React, { useEffect, useRef } from "react";
import p5 from "p5";
import { cloneDeep } from 'lodash';
import { useSelector, useDispatch } from "react-redux";
import { drawScaleLineV2 } from "./ScaleLine";
import Slider from "../helpers/Slider";
import {
  geneHeight,
  backgroundColour,
  baseline,
  componentWidth,
} from "../constants";
import { getStartCoord, getWidth } from "../helpers/calculatePosition";
import { calculateIntervals } from "../helpers/calculateIntervals";
import { changeSliderPosition, changeSliderRepeatSelection, changeSliderGeneSelection, changeSliderWidth, changeSliderMethylationSelection, changeRepeatStart, changeRepeatEnd } from "../redux/reducers/sliderSlice";
import { changePointerPosition, changePointerSelection } from "../redux/reducers/pointerSlice";
import formatActionPayload from "../redux/formatActionPayload";
import { moveSlider, resizeRightSideSlider, resizeLeftSideSlider } from "../redux/actions";

const Chromosome = (props) => {
  const containerRef = useRef();
  const dispatch = useDispatch();

  const genome = props.genomeData.genome;

  const selectedSecondaryTracks = useSelector(state => state.dashboard.secondaryTrack);
  const selectedChromosomes = useSelector(state => state.chromosomes);
  const chromosome = selectedChromosomes.current[genome];
  const sliderInformation = useSelector(state => state.slider);
  let sliderPosition = sliderInformation.position[genome]
  let sliderWidth = sliderInformation.width[genome]


  const Sketch = (p) => {
    let slider, minPos, maxPos, intervals, componentHeight, gdm;
    // map preserves insertion order
    let images = new Map();
    let secondaryTrack = new Map();

    p.preload = () => {
      for (let val in props.preprocessedImage) {
        images.set(val, p.loadImage(props.preprocessedImage[val]));
        //images[val] = p.loadImage(props.preprocessedImage[val]);
      }
      for (const [key, value] of props.preprocessedGDM) {
        secondaryTrack.set(key, p.loadImage(value));
      }
    }

    p.setup = () => {
      componentHeight = (images.size + secondaryTrack.size) * geneHeight + 5;
      p.createCanvas(componentWidth, componentHeight+baseline+5);
      slider = new Slider(sliderPosition, 0, sliderWidth, componentHeight, p);

      minPos = props.genes[0]["start"];
      maxPos = props.genes[props.genes.length - 1]["end"];
      intervals = calculateIntervals(1, maxPos, 7);

      p.noLoop();
    };

    p.draw = () => {
      p.background(backgroundColour);

      // determine the new list of repeats under the slider
      slider.resetSelectedGenes();
      selectGenes();

      const repeats = slider.getSliderRepeats();
      let s = parseInt((sliderPosition / componentWidth) * props.genomeData.maxChromosomePositions[chromosome]);
      if (s === 0) s = 1;
      const tempStart = {"genome": genome, "start": s};
      const tempEnd = {"genome": genome, "end": repeats[repeats.length-1].end};
      dispatch(changeRepeatStart(formatActionPayload(genome, s)));
      dispatch(changeRepeatEnd(formatActionPayload(genome, repeats[repeats.length-1].end)));

      //let tempRepeats = {...sliderInformation.repeatSelection, [genome]: slider.getSliderRepeats()};
      const tempRepeats = {"genome": genome, "selection": slider.getSliderRepeats()};
      const tempGenes = {"genome": genome, "geneSelection": slider.getSliderGenes()};
      //let tempGenes = {...sliderInformation.geneSelection, [genome]: slider.getSliderGenes()};
      // let tempMethylation = {...sliderInformation.methylationSelection, [genome]: slider.getMethylation()};
      const tempMethylation = {"genome": genome, "methylationSelection": slider.getMethylation()};
      dispatch(changeSliderRepeatSelection(formatActionPayload(genome, slider.getSliderRepeats())));
      // deep clone to prevent mutation
      dispatch(changeSliderGeneSelection(cloneDeep(formatActionPayload(genome, slider.getSliderGenes()))));
      dispatch(changeSliderMethylationSelection(cloneDeep(formatActionPayload(genome, slider.getMethylation()))));

      //dispatch(changePointerSelection({"genome": genome, }))

      placeImageStack();

      drawScaleLineV2(p, intervals, componentHeight + baseline);
      slider.display();
    };

    p.mouseMoved = () => {
      if (typeof slider !== "undefined") {
        const {left, top, width, height} = slider.getSliderParameters();
        if (p.mouseY <= top + height && p.mouseY >= top) {
          // cursor is in position to move slider
          if (p.mouseX < left + width - 3 && p.mouseX >= left + 3) {
            p.cursor('grab');
          }
          // cursor is in position to resize slider on the right
          else if (p.mouseX >= left + width - 3 && p.mouseX <= left + width + 3) {
            p.cursor(p.MOVE);
          }
          // cursor is in position to resize slider on the left
          else if (p.mouseX <= left + 3 && p.mouseX >= left - 3) {
            p.cursor(p.MOVE);
          }
          // cursor position doesn't affect slider
          else {
            p.cursor(p.ARROW);
          }
        }
      }
    }

    p.mousePressed = () => {
      if (typeof slider !== "undefined") {
        const {left, top, width, height} = slider.getSliderParameters();
        placeImageStack();

        slider.display();
        // mouseX and mouseY are on the og canvas, so have to add in the img's top Y coord
        if (p.mouseY <= top + height && p.mouseY >= top) {
          // slider move
          if (p.mouseX < left + width - 3 && p.mouseX >= left + 3) {
            slider.setSelected();
            p.cursor('grab');
          }
          // slider resize on right side
          else if (p.mouseX >= left + width - 3 && p.mouseX <= left + width + 3) {
            slider.setResized();
            p.cursor(p.MOVE);
          } 
          // slider resize on left side
          else if (p.mouseX <= left + 3 && p.mouseX >= left - 3) {
            slider.setResized();
            slider.setLeftResized();
            p.cursor(p.MOVE);
          }
        }
      }
    };

    p.mouseDragged = () => {
      if (typeof slider !== "undefined") {
        const {selected, isDragged, isReadyToResize, isResized, isLeftResize} = slider.getSliderStates();
        p.background(backgroundColour);
        placeImageStack();

        drawScaleLineV2(p, intervals, componentHeight + baseline);
        // call display twice to prevent weird drawing issues
        slider.display();
        // slider move
        if (selected) {
          slider.move(p.mouseX, componentWidth);
          slider.display();
        } 
        // slider resize on the right
        else if (isReadyToResize && !isLeftResize) {
          slider.rightResize(p.mouseX, componentWidth);
        } 
        // slider resize on the left
        else if (isReadyToResize && isLeftResize) {
          slider.leftResize(p.mouseX);
        }
      }
    };

    p.mouseReleased = () => {
      if (typeof slider !== "undefined") {
        const {selected, isDragged, isReadyToResize, isResized, isLeftResize} = slider.getSliderStates();
        const {left, top, width} = slider.getSliderParameters();
        //let tempPosition = {...sliderInformation.position, [genome]: left};
        let tempPosition = formatActionPayload(genome, left);
        // let tempWidth = {...sliderInformation.width, [genome]: width};
        let tempWidth = formatActionPayload(genome, width);
        // slider was moved
        if (selected && isDragged) {
          //dispatch(changeSliderPosition())
          //props.handleSliderMove(left, genome);
          dispatch(moveSlider(genome, left));
          // dispatch(changeSliderPosition(tempPosition));
          // dispatch(changePointerPosition(formatActionPayload(genome, -10)));
          // dispatch(changePointerSelection(formatActionPayload(genome, [])));
          slider.unsetSelected();
        } 
        // slider is resized on the right side
        else if (isReadyToResize && isResized && !isLeftResize) {
          dispatch(resizeRightSideSlider(genome, width))
          // dispatch(changeSliderWidth(tempWidth));
          // dispatch(changePointerPosition(formatActionPayload(genome, -10)));
          // dispatch(changePointerSelection(formatActionPayload(genome, [])));
          //props.handleSliderResize(width, genome);
          slider.unsetResized();
        } 
        // slider is resized on the left size
        else if (isReadyToResize && isResized && isLeftResize) {
          dispatch(resizeLeftSideSlider(genome, left, width));
          // dispatch(changeSliderPosition(tempPosition));
          // dispatch(changeSliderWidth(tempWidth));
          // dispatch(changePointerPosition(formatActionPayload(genome, -10)));
          // dispatch(changePointerSelection(formatActionPayload(genome, [])));
          // props.handleSliderResize(width, genome);
          // props.handleSliderMove(left, genome);
          slider.unsetLeftResized();
        } 
        else slider.unsetSelected();
        
        p.redraw();
        p.cursor(p.ARROW);
      }
    };

    const selectGenes = () => {
      for (let i = 0; i < props.genes.length; i++) {
        var start = getStartCoord(
          props.genes[i],
          componentWidth,
          props.genomeData.maxChromosomePositions[chromosome]
        );
        var end =
          start +
          getWidth(
            props.genes[i],
            componentWidth,
            props.genomeData.maxChromosomePositions[chromosome]
          );
        // pass boundary checking to the slider
        slider.determineSelectedRepeats(props.genes[i], start, end);
      }

      let genes = props.genomeData.geneData[chromosome];
      for (let i=0; i<genes.length; i++) {
        let gdmStart = getStartCoord(genes[i], componentWidth, props.genomeData.maxChromosomePositions[chromosome]);
        let gdmEnd = gdmStart + getWidth(genes[i], componentWidth, props.genomeData.maxChromosomePositions[chromosome]);
        slider.determineSelectedGenes(genes[i], gdmStart, gdmEnd);
      }

      let methylation = props.genomeData.methylationData[chromosome];
      for (let i=0; i<methylation.length; i++) {
        let mStart = getStartCoord(methylation[i], componentWidth, props.genomeData.maxChromosomePositions[chromosome]);
        let mEnd = mStart + getWidth(methylation[i], componentWidth, props.genomeData.maxChromosomePositions[chromosome]);
        slider.determineSelectedMethylation(methylation[i], mStart, mEnd);
      }
    }

    const placeImageStack = () => {
      // use for-loop to preserve ordering of images 
      let count = 0;
      for (const [key, value] of images.entries()) {
        p.image(value, 0, count*geneHeight, componentWidth, geneHeight);
        count += 1
        //p.image(images[imageKeys[i]], 0, i*geneHeight, componentWidth, geneHeight);
      }
      count = 0;
      for (const [key, val] of secondaryTrack.entries()) {
        p.image(val, 0, (geneHeight*images.size + geneHeight*count)+5, componentWidth, geneHeight);
        count += 1
      }
      //p.image(gdm, 0, geneHeight*images.size, componentWidth, geneHeight);
    }
  };

  useEffect(() => {
    // list the variables this sketch needs to reinitialize for
    let inst = new p5(Sketch, containerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => inst.remove();
  }, [props.clade, chromosome, props.genes, sliderWidth, sliderPosition, props.genome,
      props.completeFilter, selectedSecondaryTracks]);

  return <div ref={containerRef}> </div>;
};
export default Chromosome;
